/** Just the shape of a hast node this needs. */
export interface HastNode {
  readonly type: string;
  readonly tagName?: string;
  readonly value?: string;
  readonly children?: readonly HastNode[];
}

export type ParagraphKind = 'label' | 'takeaway' | 'body';

/** A takeaway is a sentence, not a phrase: shorter bold-only lines are
 *  emphasis, not a summary worth a callout. */
const TAKEAWAY_MIN_CHARS = 60;

/**
 * Models write structure as bold paragraphs instead of headings. Telling the
 * two common shapes apart gives the reply the hierarchy it was meant to
 * have:
 *  - `**What's driving the shift:**` (bold only, ends in a colon) introduces
 *    what follows: a section label.
 *  - `**So the trend isn't just more claims — …**` (bold only, a full
 *    sentence) states the conclusion: a takeaway.
 * Anything else is body text.
 */
export function classifyParagraph(node: HastNode | undefined): ParagraphKind {
  const children = (node?.children ?? []).filter(
    (child) => !(child.type === 'text' && (child.value ?? '').trim() === '')
  );
  const only = children.length === 1 ? children[0] : undefined;
  if (only?.type !== 'element' || only.tagName !== 'strong') {
    return 'body';
  }
  const text = textOf(only).trim();
  if (text.endsWith(':')) {
    return 'label';
  }
  return text.length >= TAKEAWAY_MIN_CHARS ? 'takeaway' : 'body';
}

function textOf(node: HastNode): string {
  if (node.type === 'text') {
    return node.value ?? '';
  }
  return (node.children ?? []).map(textOf).join('');
}
